import React, { useState } from 'react';
import { FaQuestionCircle, FaChevronDown, FaPlane, FaUsers, FaRobot, FaCreditCard, FaLock, FaGlobe, FaMobileAlt, FaMapMarkedAlt, FaSearch, FaMagic } from 'react-icons/fa';
import SEO from './SEO';
import InternalLinks from './InternalLinks';
import BASE_URL, { SEO_BASE_URL } from '../config';

const FAQ = ({ onBack, onNavigate }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Questions', icon: FaQuestionCircle },
    { id: 'getting-started', label: 'Getting Started', icon: FaPlane },
    { id: 'features', label: 'Features', icon: FaMagic },
    { id: 'account', label: 'Account & Billing', icon: FaCreditCard },
    { id: 'privacy', label: 'Privacy & Security', icon: FaLock },
  ];

  const faqs = [
    // Getting Started
    {
      category: 'getting-started',
      question: 'What is ItinerarySeek?',
      answer: 'ItinerarySeek is an AI-powered travel planning platform that helps you create personalized, detailed itineraries in seconds. Simply enter your destination, travel dates, and preferences, and our AI generates a comprehensive day-by-day travel plan with activities, restaurants, and points of interest tailored to your interests.',
      link: { text: 'Learn more about us', href: '/about', view: 'about' }
    },
    {
      category: 'getting-started',
      question: 'How do I create my first itinerary?',
      answer: 'Creating your first itinerary is easy! After signing up or logging in, click "Create New Itinerary" from your dashboard. Enter your destination city, travel dates, and select your interests (adventure, culture, food, relaxation, etc.). You can also set your budget level and pace preference. Click "Generate" and our AI will create a personalized itinerary within seconds.',
      link: { text: 'See our step-by-step guide', href: '/howto', view: 'howto' }
    },
    {
      category: 'getting-started',
      question: 'Do I need to create an account to use ItinerarySeek?',
      answer: 'Yes, you need to create a free account to generate and save itineraries. This allows you to access your trips from any device, share them with travel companions, and make edits at any time. Registration is quick and only requires your email address.',
      link: { text: 'Create your free account', href: '/register', view: 'register' }
    },
    {
      category: 'getting-started',
      question: 'Is ItinerarySeek free to use?',
      answer: 'ItinerarySeek offers a free tier that allows you to create a limited number of itineraries per month. For unlimited itinerary generation, advanced features like group trip planning, and premium support, we offer affordable subscription plans.',
      link: { text: 'View pricing plans', href: '/pricing', view: 'pricing' }
    },
    {
      category: 'getting-started',
      question: 'What destinations does ItinerarySeek support?',
      answer: 'ItinerarySeek supports destinations worldwide! Our AI has knowledge of cities, towns, and tourist destinations across all continents. Whether you\'re planning a trip to Paris, Tokyo, New York, or a lesser-known destination, we can help create your perfect itinerary.'
    },

    // Features
    {
      category: 'features',
      question: 'How does the AI generate itineraries?',
      answer: 'Our AI uses advanced machine learning models trained on extensive travel data, including popular attractions, local restaurants, cultural experiences, and practical logistics. It considers factors like opening hours, travel time between locations, your stated preferences, and seasonal recommendations to create optimized, realistic itineraries.'
    },
    {
      category: 'features',
      question: 'Can I customize the generated itinerary?',
      answer: 'Absolutely! Every generated itinerary is fully editable. You can drag and drop activities to rearrange them, add your own items, remove suggestions you\'re not interested in, adjust times, and add personal notes. The itinerary is your canvas to personalize.'
    },
    {
      category: 'features',
      question: 'What is group trip planning?',
      answer: 'Group trip planning allows you to invite friends and family to collaborate on your itinerary. Everyone can view the trip details, suggest changes, and participate in a built-in group chat. It\'s perfect for coordinating family vacations, friend getaways, or group tours.'
    },
    {
      category: 'features',
      question: 'Does ItinerarySeek provide directions between locations?',
      answer: 'Yes! Each itinerary includes turn-by-turn directions between activities. You can view walking, driving, or public transit routes depending on your preference. Directions can be exported or opened directly in your preferred maps app.'
    },
    {
      category: 'features',
      question: 'Can I track expenses for my trip?',
      answer: 'Yes, our expense tracker lets you log and categorize trip expenses. You can set a budget, add expenses as you go, and see real-time summaries of your spending. This feature helps you stay on budget throughout your trip.'
    },
    {
      category: 'features',
      question: 'What is the packing list feature?',
      answer: 'Based on your destination, travel dates, and planned activities, our AI can generate a suggested packing list. You can customize it by adding or removing items and check them off as you pack. Never forget essential items again!'
    },
    {
      category: 'features',
      question: 'Can I download or print my itinerary?',
      answer: 'Yes! You can download your itinerary as a PDF that includes all your daily plans, directions, reservations, and notes. This is perfect for offline access or printing a physical copy to bring on your trip.'
    },
    {
      category: 'features',
      question: 'Does ItinerarySeek show weather information?',
      answer: 'Yes, we display weather forecasts for your destination during your travel dates. This helps you plan appropriate activities and pack the right clothing. Weather data is updated regularly to ensure accuracy.'
    },
    {
      category: 'features',
      question: 'Can I share my itinerary with others?',
      answer: 'Yes! You can generate a shareable link to your itinerary that allows others to view it without needing an account. For collaborative editing, you can invite others to join as group members with full editing access.'
    },
    {
      category: 'features',
      question: 'Is there a mobile app?',
      answer: 'ItinerarySeek is a progressive web app (PWA), meaning you can install it on your phone directly from your browser. It works offline and provides a native app-like experience. Simply visit our website on your mobile browser and select "Add to Home Screen."'
    },

    // Account & Billing
    {
      category: 'account',
      question: 'How do I upgrade my account?',
      answer: 'To upgrade, go to your Account settings and click on "Subscription" or visit our Pricing page. Select the plan that best fits your needs and complete the secure checkout process. Your new features will be available immediately.'
    },
    {
      category: 'account',
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, MasterCard, American Express) and PayPal. All payments are processed securely through our payment partners with industry-standard encryption.'
    },
    {
      category: 'account',
      question: 'Can I cancel my subscription?',
      answer: 'Yes, you can cancel your subscription at any time from your Account settings. You\'ll continue to have access to premium features until the end of your current billing period. We don\'t offer refunds for partial months, but you won\'t be charged again after cancellation.'
    },
    {
      category: 'account',
      question: 'How do I delete my account?',
      answer: 'To delete your account, go to Account settings and select "Delete Account." This will permanently remove all your data including saved itineraries, preferences, and personal information. This action cannot be undone, so please download any itineraries you wish to keep first.'
    },
    {
      category: 'account',
      question: 'I forgot my password. How do I reset it?',
      answer: 'Click "Forgot Password" on the login page and enter your email address. We\'ll send you a secure link to reset your password. The link expires after 24 hours for security purposes. If you don\'t receive the email, check your spam folder or contact support.'
    },

    // Privacy & Security
    {
      category: 'privacy',
      question: 'How is my personal data protected?',
      answer: 'We take data security seriously. All data is encrypted in transit using TLS and at rest using industry-standard encryption. We never sell your personal information to third parties. Our servers are protected by enterprise-grade security measures, and we regularly conduct security audits.'
    },
    {
      category: 'privacy',
      question: 'What data do you collect?',
      answer: 'We collect only the data necessary to provide our services: your email address, name, and the itinerary data you create. We also collect anonymous usage analytics to improve our service. You can review our complete Privacy Policy for detailed information about data collection and use.'
    },
    {
      category: 'privacy',
      question: 'Can I export my data?',
      answer: 'Yes, you have full control over your data. You can download all your itineraries as PDFs, and you can request a complete export of your account data at any time by contacting our support team.'
    },
    {
      category: 'privacy',
      question: 'Do you use cookies?',
      answer: 'We use essential cookies to keep you logged in and remember your preferences. We also use analytics cookies to understand how users interact with our platform so we can improve it. You can manage cookie preferences in your browser settings.'
    },
    {
      category: 'privacy',
      question: 'Is my itinerary data shared with anyone?',
      answer: 'Your itineraries are private by default and only visible to you and anyone you explicitly share them with. We do not share your travel plans or personal data with advertisers or third parties. Group members you invite can see shared itinerary data.'
    },
  ];

  const filteredFaqs = activeCategory === 'all'
    ? faqs
    : faqs.filter(faq => faq.category === activeCategory);

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Generate FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <SEO
        title="FAQ - Frequently Asked Questions"
        description="Find answers to frequently asked questions about ItinerarySeek. Learn how to create AI-powered travel itineraries, manage your account, collaborate on group trips, and more."
        canonical={`${SEO_BASE_URL}/faq`}
      />

      {/* FAQ Schema */}
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-primary-50">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-200 to-secondary-200 rounded-full opacity-30 blur-3xl animate-pulse"></div>
            <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-secondary-200 to-primary-200 rounded-full opacity-30 blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
            {/* Back Button */}
            <button
              onClick={onBack}
              className="mb-8 flex items-center gap-2 text-neutral-600 hover:text-primary-500 transition-colors group"
            >
              <span className="transform group-hover:-translate-x-1 transition-transform">&larr;</span>
              Back
            </button>

            {/* Main Hero */}
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-4 mb-6">
                <span className="h-px w-12 bg-gradient-to-r from-transparent to-primary-400"></span>
                <span className="text-primary-600 text-sm uppercase tracking-widest font-medium flex items-center gap-2">
                  <FaQuestionCircle className="text-secondary-500" />
                  Help Center
                </span>
                <span className="h-px w-12 bg-gradient-to-l from-transparent to-primary-400"></span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-primary-500 via-secondary-500 to-primary-500 bg-clip-text text-transparent">
                  Frequently Asked
                </span>
                <br />
                <span className="text-neutral-800">Questions</span>
              </h1>

              <p className="text-lg text-neutral-600 max-w-2xl mx-auto leading-relaxed">
                Everything you need to know about ItinerarySeek. Can't find the answer you're looking for?
                Feel free to <button onClick={() => onBack && window.location.assign('/contact')} className="text-primary-500 hover:text-primary-600 font-medium underline">contact our support team</button>.
              </p>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {categories.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => {
                      setActiveCategory(category.id);
                      setOpenIndex(null);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      activeCategory === category.id
                        ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg'
                        : 'bg-white text-neutral-600 hover:bg-neutral-100 border border-neutral-200'
                    }`}
                  >
                    <Icon className="text-sm" />
                    {category.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* FAQ List */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <div className="space-y-4">
            {filteredFaqs.map((faq, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden ${
                  openIndex === index
                    ? 'border-primary-200 shadow-lg'
                    : 'border-neutral-100 shadow-card hover:shadow-card-hover hover:border-neutral-200'
                }`}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 text-left flex items-start justify-between gap-4"
                >
                  <span className={`font-semibold transition-colors ${
                    openIndex === index ? 'text-primary-600' : 'text-neutral-800'
                  }`}>
                    {faq.question}
                  </span>
                  <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                    openIndex === index
                      ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white rotate-180'
                      : 'bg-neutral-100 text-neutral-500'
                  }`}>
                    <FaChevronDown className="text-sm" />
                  </span>
                </button>

                <div
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-5 pt-0">
                    <div className="h-px bg-gradient-to-r from-primary-100 via-secondary-100 to-transparent mb-4"></div>
                    <p className="text-neutral-600 leading-relaxed">
                      {faq.answer}
                      {faq.link && (
                        <>
                          {' '}
                          <a
                            href={faq.link.href}
                            onClick={(e) => { e.preventDefault(); onNavigate && onNavigate(faq.link.view); }}
                            className="text-primary-600 hover:text-primary-700 font-medium hover:underline"
                          >
                            {faq.link.text} →
                          </a>
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* No Results */}
          {filteredFaqs.length === 0 && (
            <div className="text-center py-12">
              <FaSearch className="text-4xl text-neutral-300 mx-auto mb-4" />
              <p className="text-neutral-500">No questions found in this category.</p>
            </div>
          )}
        </div>

        {/* Internal Links for SEO */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <InternalLinks currentPage="faq" onNavigate={onNavigate} variant="compact" />
        </div>

        {/* Still Need Help CTA */}
        <div className="bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-500 relative overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-10 left-10 w-20 h-20 border-2 border-white/20 rounded-full"></div>
            <div className="absolute bottom-10 right-10 w-32 h-32 border-2 border-white/20 rounded-full"></div>
            <FaPlane className="absolute top-1/4 right-1/4 text-white/10 text-6xl transform rotate-12" />
          </div>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center relative">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Still have questions?
            </h2>
            <p className="text-white/90 mb-8 max-w-xl mx-auto">
              Our friendly support team is here to help. Reach out and we'll get back to you as soon as possible.
            </p>
            <button
              onClick={() => onBack && window.location.assign('/contact')}
              className="inline-flex items-center justify-center gap-2 bg-white text-primary-600 px-8 py-4 rounded-xl font-semibold hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <FaGlobe />
              Contact Support
            </button>
          </div>
        </div>

        {/* Quick Links Footer */}
        <div className="bg-white py-12 border-t border-neutral-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="p-4">
                <FaRobot className="text-3xl text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-neutral-800 mb-1">AI-Powered</h3>
                <p className="text-sm text-neutral-500">Smart itineraries in seconds</p>
              </div>
              <div className="p-4">
                <FaUsers className="text-3xl text-secondary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-neutral-800 mb-1">Group Planning</h3>
                <p className="text-sm text-neutral-500">Collaborate with friends</p>
              </div>
              <div className="p-4">
                <FaMapMarkedAlt className="text-3xl text-accent-500 mx-auto mb-3" />
                <h3 className="font-semibold text-neutral-800 mb-1">Detailed Routes</h3>
                <p className="text-sm text-neutral-500">Turn-by-turn directions</p>
              </div>
              <div className="p-4">
                <FaMobileAlt className="text-3xl text-adventure-500 mx-auto mb-3" />
                <h3 className="font-semibold text-neutral-800 mb-1">Works Offline</h3>
                <p className="text-sm text-neutral-500">Access anywhere</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQ;
